import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";

export default function Videos() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const videos = [
    {
      id: "75uNuPQ02Tg",
      title: "Yoga for Hair Fall",
      description: "Natural remedies through yoga",
    },
    {
      id: "EpaQipkd8ZU",
      title: "Yoga for Glowing Skin",
      description: "Face yoga and skincare tips",
    },
    {
      id: "vJSLmITgYwg",
      title: "Yoga for Shoulders",
      description: "Easy and beneficial exercises",
    },
  ];

  return (
    <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-sage-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-earth-100 rounded-full blur-3xl opacity-30"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-sage-100 text-sage-700 rounded-full text-sm font-semibold mb-4">
            Watch & Learn
          </span>
          <h2 className="section-title">Our Video Library</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Learn yoga techniques and wellness tips from our expert instructors
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 0.8,
            ease: "easeInOut",
          }}
          className="w-full max-w-5xl mx-auto"
        >
        {/* Carousel for Small-Sized Screen */}
        <CarouselProvider
          className="relative block sm:hidden"
          naturalSlideWidth={100}
          isIntrinsicHeight={true}
          totalSlides={3}
          visibleSlides={1}
          step={1}
          infinite={true}
        >
          <div className="js-flickity flex justify-center items-center">
            <ButtonBack
              role="button"
              aria-label="slide backward"
              className="w-12 h-12 md:w-14 md:h-14 rounded-full flex justify-center items-center bg-gradient-to-r from-sage-600 to-teal-600 hover:from-sage-700 hover:to-teal-700 absolute z-30 left-0 ml-4 focus:outline-none shadow-lg cursor-pointer transform hover:scale-110 transition-all duration-300"
              id="prev"
            >
              <svg
                width={8}
                height={14}
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 1L1 7L7 13"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ButtonBack>
            <Slider>
              <Slide index={0}>
                <div className="gallery-cell lg:mr-7 mr-6 lg:w-1/2 sm:w-96 w-full h-full">
                  <iframe
                    width="400"
                    height="200"
                    src="https://www.youtube.com/embed/75uNuPQ02Tg"
                    title="Yoga for Hair fall |"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                  ></iframe>
                </div>
              </Slide>
              <Slide index={1}>
                <div className="gallery-cell lg:mr-7 mr-6 lg:w-1/2 sm:w-96 w-full h-full">
                  <iframe
                    width="400"
                    height="200"
                    src="https://www.youtube.com/embed/EpaQipkd8ZU"
                    title="#yoga #for #glowingskin #doublechin #facetone | #skincare #skin #face #facecare #facecaretips #care"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                  ></iframe>
                </div>
              </Slide>
              <Slide index={2}>
                <div className="gallery-cell lg:mr-7 mr-6 lg:w-1/2 sm:w-96 w-full h-full">
                  <iframe
                    width="400"
                    height="200"
                    src="https://www.youtube.com/embed/vJSLmITgYwg"
                    title="4 Easy &amp; Very Beneficial Yoga For Shoulders |"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                  ></iframe>
                </div>
              </Slide>
            </Slider>
            <ButtonNext
              role="button"
              aria-label="slide forward"
              className="w-12 h-12 md:w-14 md:h-14 rounded-full flex justify-center items-center bg-gradient-to-r from-sage-600 to-teal-600 hover:from-sage-700 hover:to-teal-700 absolute z-30 right-0 mr-4 focus:outline-none shadow-lg cursor-pointer transform hover:scale-110 transition-all duration-300"
              id="next"
            >
              <svg
                width={8}
                height={14}
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L7 7L1 13"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ButtonNext>
          </div>
        </CarouselProvider>

        {/* Carousel for Medium and Large-Sized Screen */}
        <CarouselProvider
          className="relative hidden sm:block"
          naturalSlideWidth={100}
          isIntrinsicHeight={true}
          totalSlides={3}
          visibleSlides={1}
          step={1}
          infinite={true}
          currentSlide={1}
        >
          <div className="js-flickity flex justify-center items-center">
            <ButtonBack
              role="button"
              aria-label="slide backward"
              className="w-12 h-12 md:w-14 md:h-14 rounded-full flex justify-center items-center bg-gradient-to-r from-sage-600 to-teal-600 hover:from-sage-700 hover:to-teal-700 absolute z-30 left-0 ml-4 focus:outline-none shadow-lg cursor-pointer transform hover:scale-110 transition-all duration-300"
              id="prev"
            >
              <svg
                width={8}
                height={14}
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 1L1 7L7 13"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ButtonBack>
            <Slider className="carousel__sliderLarge">
              <Slide className="carousel__inner-slideLarge" index={0}>
                <iframe
                  width="600"
                  height="400"
                  src="https://www.youtube.com/embed/75uNuPQ02Tg"
                  title="Yoga for Hair fall |"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                ></iframe>
              </Slide>
              <Slide className="carousel__inner-slideLarge" index={1}>
                <iframe
                  width="600"
                  height="400"
                  src="https://www.youtube.com/embed/EpaQipkd8ZU"
                  title="#yoga #for #glowingskin #doublechin #facetone | #skincare #skin #face #facecare #facecaretips #care"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                ></iframe>
              </Slide>
              <Slide className="carousel__inner-slideLarge" index={2}>
                <iframe
                  width="600"
                  height="400"
                  src="https://www.youtube.com/embed/vJSLmITgYwg"
                  title="4 Easy &amp; Very Beneficial Yoga For Shoulders |"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                ></iframe>
              </Slide>
            </Slider>
            <ButtonNext
              role="button"
              aria-label="slide forward"
              className="w-12 h-12 md:w-14 md:h-14 rounded-full flex justify-center items-center bg-gradient-to-r from-sage-600 to-teal-600 hover:from-sage-700 hover:to-teal-700 absolute z-30 right-0 mr-4 focus:outline-none shadow-lg cursor-pointer transform hover:scale-110 transition-all duration-300"
              id="next"
            >
              <svg
                width={8}
                height={14}
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L7 7L1 13"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ButtonNext>
          </div>
        </CarouselProvider>
        </motion.div>
      </div>

      <style>
        {`
          .gallery-cell {
            height: 386px;
            padding-right: 15px;
          }

          @media (min-width: 300px) and (max-width: 420px) {
            .gallery-cell {
              height: 286px !important;
            }
          }

          @media (max-width: 640px) {
            .gallery-cell {
              padding-right: 0;
            }
          }

          .carousel__sliderLarge {
            padding-left: 10%;
            padding-right: 10%;
          }

          .carousel__inner-slideLarge {
            width: calc(100% - 20px);
            height: calc(100% - 20px);
            left: 10px;
            top: 10px;
          }

          iframe {
            border-radius: 20px;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }
        `}
      </style>
    </section>
  );
}
